import { useCallback } from 'react'
import { useFlag } from '@openfeature/react-sdk'

import { selectCatchupBookkeepingFees } from '@/selectors/catchupBookkeeping.selectors'
import { useFetchResponse, useReselector } from '@/utils/sharedHooks'
import { isProduction } from '@/utils/envHelpers'
import {
  AnnualCommitmentDiscount,
  getAnnualCommitmentPriceIdFromProductId,
  prodTrialCatchupBkPriceId,
  testTrialCatchupBkPriceId,
} from '@/constants/pricingConstants'
import { fetchCatchUpBKMonths } from '@/components/CatchUpBookkeepingStatus/catchUpBookkeepingStatus.slice'
import {
  FEATURE_FLAG_KEYS,
  HISTORICAL_BOOKKEEPING_FEE_COHORTS,
} from '@/features/open-feature'
import { StripeProduct } from '@/actions/settings/billingActions'
import { selectHasAnnualCommitment } from '../../settings/sections/self-serve-cancellation/lib/userCancellation.selectors'
import { centsToCurrency } from '@/utils/currencyHelpers'

// Determines how much a user should pay for the catchup bookkeeping price
export const useCatchupBookkeepingPrice = () => {
  const cohortFlag = useFlag(
    FEATURE_FLAG_KEYS.catchupBookkeepingFee,
    HISTORICAL_BOOKKEEPING_FEE_COHORTS.cohort2
  )
  const catchUpBKMonths = useFetchResponse(fetchCatchUpBKMonths)

  const catchupBookkeepingPrice = useReselector(
    selectCatchupBookkeepingFees,
    catchUpBKMonths
  )

  // We don't want to show the fee if the user has no catchup bookkeeping months
  if (
    !catchupBookkeepingPrice.amount ||
    catchupBookkeepingPrice.amount.value === 0 ||
    cohortFlag.value === HISTORICAL_BOOKKEEPING_FEE_COHORTS.none
  ) {
    return undefined
  }

  return {
    ...catchupBookkeepingPrice,
    freeTrialPriceId: isProduction()
      ? prodTrialCatchupBkPriceId
      : testTrialCatchupBkPriceId,
  }
}

export const useAnnualCommitmentPrice = () => {
  const hasAnnualCommitment = useReselector(selectHasAnnualCommitment)

  const getAnnualCommitmentPriceId = useCallback((productId: string) => {
    return getAnnualCommitmentPriceIdFromProductId(
      productId,
      AnnualCommitmentDiscount.TEN_PERCENT
    )
  }, [])

  const priceAsCurrency = useCallback(
    (price?: { productId: string; unitAmountInCents: number | null }) => {
      if (hasAnnualCommitment && price?.productId) {
        // TODO: Clean up after annual commitment logic after expieriment
        // this is a hack to get the annual commit price without changing the eligible product api logic
        const annualCommitmentPrice = getAnnualCommitmentPriceId(
          price.productId
        )
        if (annualCommitmentPrice) {
          return centsToCurrency(annualCommitmentPrice.amountInCents)
        }
      }
      return centsToCurrency(price?.unitAmountInCents) || 0
    },
    [hasAnnualCommitment, getAnnualCommitmentPriceId]
  )

  const productPriceAsCurrency = useCallback(
    (product?: StripeProduct) =>
      priceAsCurrency(
        product && {
          productId: product.id,
          unitAmountInCents: product.default_price.unit_amount,
        }
      ),
    [priceAsCurrency]
  )

  return {
    getAnnualCommitmentPriceId,
    priceAsCurrency,
    productPriceAsCurrency,
  }
}
